/**
 * Person Attribute Constants
 *
 * ⚠️  IMMUTABLE SCHEMA: These values are persisted in DynamoDB.
 * SAFE: Rename keys, add new values
 * UNSAFE: Change string values (requires migration)
 */

export const Gender = Object.freeze({
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'OTHER'
});

export const AgeGroup = Object.freeze({
  YOUNG: 'YOUNG',
  THIRTIES: 'THIRTIES',
  FORTIES: 'FORTIES',
  FIFTIES: 'FIFTIES',
  SIXTIES: 'SIXTIES',
  SEVENTYPLUS: 'SEVENTYPLUS'
});

export const FacialHair = Object.freeze({
  NONE: 'NONE',
  MOUSTACHE: 'MOUSTACHE',
  BEARD: 'BEARD',
  GOATEE: 'GOATEE'
});

export const Glasses = Object.freeze({
  YES: 'YES',
  NO: 'NO'
});

export const Height = Object.freeze({
  SHORT: 'SHORT',
  AVG: 'AVG',
  TALL: 'TALL',
  GIANT: 'GIANT'
});

export const Build = Object.freeze({
  SKINNY: 'SKINNY',
  SLIM: 'SLIM',
  AVG: 'AVG',
  TUBBY: 'TUBBY',
  FAT: 'FAT',
  HUGE: 'HUGE'
});

export const SkinTone = Object.freeze({
  WHITE: 'WHITE',
  ASIAN: 'ASIAN',
  BLACK: 'BLACK'
});

export const Dress = Object.freeze({
  FORMAL: 'FORMAL',
  BUSINESS: 'BUSINESS',
  CASUAL: 'CASUAL'
});

export const InterestLevel = Object.freeze({
  NONE: 'NONE',
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
});

export const HairColor = Object.freeze({
  BLONDE: 'BLONDE',
  BROWN: 'BROWN',
  BLACK: 'BLACK',
  GREY: 'GREY',
  RED: 'RED'
});

export const HairStyle = Object.freeze({
  BALD: 'BALD',
  SHORT: 'SHORT',
  BOB: 'BOB',
  SHOULDER: 'SHOULDER',
  LONG: 'LONG'
});

export const EyeColor = Object.freeze({
  GREY: 'GREY',
  BLUE: 'BLUE',
  GREEN: 'GREEN',
  BROWN: 'BROWN',
  HAZEL: 'HAZEL'
});

// Derived sets for validation
export const GENDERS = Object.freeze(Object.values(Gender));
export const AGE_GROUPS = Object.freeze(Object.values(AgeGroup));
export const FACIAL_HAIRS = Object.freeze(Object.values(FacialHair));
export const GLASSES_VALUES = Object.freeze(Object.values(Glasses));
export const HEIGHTS = Object.freeze(Object.values(Height));
export const BUILDS = Object.freeze(Object.values(Build));
export const SKIN_TONES = Object.freeze(Object.values(SkinTone));
export const DRESSES = Object.freeze(Object.values(Dress));
export const INTEREST_LEVELS = Object.freeze(Object.values(InterestLevel));
export const HAIR_COLORS = Object.freeze(Object.values(HairColor));
export const HAIR_STYLES = Object.freeze(Object.values(HairStyle));
export const EYE_COLORS = Object.freeze(Object.values(EyeColor));

/**
 * Metadata: Maps attribute enum types to database field names
 * Single source of truth for characteristics serialization/deserialization
 *
 * Usage:
 * - UI displays: Object.keys(CHARACTERISTICS_METADATA) → ['Gender', 'AgeGroup', ...]
 * - Save to DB: CHARACTERISTICS_METADATA[displayName].dbField → 'gender', 'agegroup', ...
 * - Load from DB: Reverse lookup to display name
 * - Get enum values: CHARACTERISTICS_METADATA[displayName].enum → Gender, AgeGroup, ...
 */
export const CHARACTERISTICS_METADATA = Object.freeze({
  Gender: { dbField: 'gender', enum: Gender },
  AgeGroup: { dbField: 'agegroup', enum: AgeGroup },
  FacialHair: { dbField: 'facialhair', enum: FacialHair },
  Glasses: { dbField: 'glasses', enum: Glasses },
  Height: { dbField: 'height', enum: Height },
  Build: { dbField: 'build', enum: Build },
  SkinTone: { dbField: 'skintone', enum: SkinTone },
  HairColor: { dbField: 'haircolor', enum: HairColor },
  HairStyle: { dbField: 'hairstyle', enum: HairStyle },
  EyeColor: { dbField: 'eyecolor', enum: EyeColor },
  Dress: { dbField: 'dress', enum: Dress }
});

/**
 * Helper: Convert person object to characteristics Map (for UI)
 * @param {Object} person - Person object from database
 * @returns {Map} Map of display name → value
 */
export function personToCharacteristicsMap(person) {
  const map = new Map();
  Object.entries(CHARACTERISTICS_METADATA).forEach(([displayName, { dbField }]) => {
    if (person[dbField] !== undefined) {
      map.set(displayName, person[dbField]);
    }
  });
  return map;
}

/**
 * Helper: Convert characteristics Map to person update object (for API)
 * @param {Map} characteristicsMap - Map of display name → value
 * @returns {Object} Object with database field names
 */
export function characteristicsMapToPerson(characteristicsMap) {
  const person = {};
  characteristicsMap.forEach((value, displayName) => {
    const metadata = CHARACTERISTICS_METADATA[displayName];
    if (metadata) {
      person[metadata.dbField] = value;
    }
  });
  return person;
}
